import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Server, Loader2, RefreshCw, CheckCircle, AlertCircle, ExternalLink, Clock, Copy, QrCode, Send, Landmark } from 'lucide-react';

const statusBadge = (status) => {
  if (status === 'connected') return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="itau-health-badge">CONNECTED</Badge>;
  if (status === 'pending_credentials') return <Badge className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="itau-health-badge">AWAITING CREDENTIALS</Badge>;
  if (status === 'auth_error') return <Badge className="bg-rose-50 text-rose-700 border border-rose-200" data-testid="itau-health-badge">AUTH ERROR</Badge>;
  if (status === 'unreachable') return <Badge className="bg-rose-50 text-rose-700 border border-rose-200" data-testid="itau-health-badge">UNREACHABLE</Badge>;
  return <Badge className="bg-slate-100 text-slate-600 border border-slate-200" data-testid="itau-health-badge">UNKNOWN</Badge>;
};

const txnStatusChip = (s) => {
  const cls = s === 'paid' || s === 'sent' ? 'bg-emerald-50 text-emerald-700'
    : s === 'created' || s === 'initiated' ? 'bg-sky-50 text-sky-700'
    : s === 'expired' ? 'bg-amber-50 text-amber-700'
    : s === 'removed' ? 'bg-slate-100 text-slate-600'
    : 'bg-rose-50 text-rose-700';
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${cls}`}>{s}</span>;
};

const FX_CURRENCIES = ['USD', 'BRL', 'EUR', 'GBP', 'SAR', 'AED'];

const fmtRemaining = (expiresAt, nowMs) => {
  const diff = Math.floor((new Date(expiresAt).getTime() - nowMs) / 1000);
  if (diff <= 0) return 'expiring…';
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

const ItauLayer = () => {
  const [config, setConfig] = useState(null);
  const [txns, setTxns] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [chargeForm, setChargeForm] = useState({ amount: 0, payer_name: '', payer_cpf: '', description: '' });
  const [chargeResult, setChargeResult] = useState(null);
  const [sendForm, setSendForm] = useState({ amount: 0, chave: '', recipient_name: '', description: '' });
  const [fxForm, setFxForm] = useState({ from_currency: 'USD', to_currency: 'BRL', amount: 1000 });
  const [fxResult, setFxResult] = useState(null);
  const [ofForm, setOfForm] = useState({ amount: 0, creditor_pix_key: '', recipient_name: '', description: '' });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, tx, cl, wh] = await Promise.all([
        api.get('/itau/config'), api.get('/itau/transactions'),
        api.get('/itau/prod-checklist'), api.get('/itau/webhook/status'),
      ]);
      setConfig(cfg.data);
      setTxns(tx.data || []);
      setChecklist(cl.data);
      setWebhookInfo(wh.data);
    } catch (e) {
      toast.error('Failed to load Itaú status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const registerWebhook = async () => {
    setBusy('webhook');
    try {
      const res = await api.post('/itau/webhook/register', {
        webhook_url: `${process.env.REACT_APP_BACKEND_URL}/api/itau/webhook`,
      });
      if (res.data.registered) toast.success('Webhook registered with Itaú');
      else if (res.data.sandbox_unsupported) toast.info('Itaú sandbox does not mock webhook registration — the call is production-ready');
      else toast.warning(`Itaú returned: ${res.data.itau_response?.status || 'error'}`);
      loadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const errDetail = (e) => {
    const d = e.response?.data?.detail;
    return typeof d === 'string' ? d : (d?.error || 'Request failed');
  };

  const createCharge = async () => {
    if (!chargeForm.amount || chargeForm.amount <= 0) { toast.error('Enter an amount > 0'); return; }
    setBusy('charge');
    try {
      const res = await api.post('/itau/pix/charge', chargeForm);
      setChargeResult(res.data);
      if (res.data.status === 'created') toast.success(`Pix charge created — ${res.data.reference}`);
      else toast.warning(`Itaú returned: ${res.data.status}`);
      loadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const sendPix = async () => {
    if (!sendForm.amount || sendForm.amount <= 0) { toast.error('Enter an amount > 0'); return; }
    if (!sendForm.chave.trim()) { toast.error('Destination Pix key required'); return; }
    setBusy('send');
    try {
      const res = await api.post('/itau/pix/send', sendForm);
      if (res.data.status === 'sent') toast.success(`Pix sent — ${res.data.reference}`);
      else toast.warning(`Itaú returned: ${res.data.status} — ${res.data.reference}`);
      loadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const getFxQuote = async () => {
    if (!fxForm.amount || fxForm.amount <= 0) { toast.error('Enter an amount > 0'); return; }
    setBusy('fx');
    try {
      const res = await api.post('/itau/fx/quote', fxForm);
      setFxResult(res.data);
      if (res.data.status === 'ok') toast.success('FX quote received');
      else toast.warning(`FX quote: ${res.data.status}`);
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const initiateOfPayment = async () => {
    if (!ofForm.amount || ofForm.amount <= 0) { toast.error('Enter an amount > 0'); return; }
    if (!ofForm.creditor_pix_key.trim()) { toast.error('Creditor Pix key required'); return; }
    setBusy('of');
    try {
      const res = await api.post('/itau/open-finance/payment', ofForm);
      if (res.data.status === 'initiated') toast.success(`Payment initiated — ${res.data.reference}`);
      else toast.warning(`Itaú returned: ${res.data.status} — ${res.data.reference}`);
      loadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const reissueCharge = async (id) => {
    setBusy(`reissue-${id}`);
    try {
      const res = await api.post(`/itau/pix/charge/${id}/reissue`);
      setChargeResult(res.data);
      if (res.data.status === 'created') toast.success(`Charge re-issued — ${res.data.reference} (new QR ready)`);
      else toast.warning(`Itaú returned: ${res.data.status}`);
      loadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const refreshStatus = async (id) => {
    setBusy(`status-${id}`);
    try {
      const res = await api.post(`/itau/status-update/${id}`);
      toast.info(`Status: ${res.data.current_status}${res.data.itau_status ? ` (Itaú: ${res.data.itau_status})` : ''}`);
      loadAll();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(''); }
  };

  const downloadPdf = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Failed to download PDF'); }
  };

  const downloadDtr = (id, reference) => downloadPdf(`/itau/dtr/${id}`, `EzyTransfer_DTR_ITAU_${reference || id}.pdf`);
  const downloadReceipt = (id, reference) => downloadPdf(`/itau/receipt/${id}`, `EzyTransfer_ITAU_Receipt_${reference || id}.pdf`);

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const health = config?.health;
  const cfg = config?.config;
  const connected = health?.status === 'connected';

  return (
    <div className="space-y-6" data-testid="itau-layer">
      {/* Connection */}
      <Card className="p-6" data-testid="itau-connection-card">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-orange-500 rounded-full" />
            <h3 className="text-xl font-semibold">Itaú — Connection</h3>
            {statusBadge(health?.status)}
            <Badge className="bg-orange-50 text-orange-700 border border-orange-200 uppercase" data-testid="itau-env-badge">{cfg?.env || 'sandbox'}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={loadAll} data-testid="itau-refresh-btn" disabled={loading}>
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="ml-1">Refresh</span>
            </Button>
            <a href="https://devportal.itau.com.br/" target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" data-testid="itau-portal-btn">
                <ExternalLink className="w-3.5 h-3.5 mr-1" /> Developer Portal
              </Button>
            </a>
          </div>
        </div>

        {health?.status === 'pending_credentials' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 mb-3" data-testid="itau-blocking-items">
            <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> To connect, set on the backend:
            </p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li className="text-[11px] text-amber-900 font-mono">ITAU_CLIENT_ID (sandbox app from devportal.itau.com.br)</li>
              <li className="text-[11px] text-amber-900 font-mono">ITAU_CLIENT_SECRET (shown only once at creation)</li>
              <li className="text-[11px] text-amber-900 font-mono">ITAU_PIX_KEY (your receiving Pix key, for charges)</li>
            </ul>
          </div>
        )}
        {health?.status === 'auth_error' && (
          <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 mb-3" data-testid="itau-auth-error">
            <p className="text-xs font-semibold text-rose-800 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> Itaú rejected the credentials — check client_id/secret or the token URL for your sandbox app.
            </p>
            <p className="text-[11px] text-rose-900 font-mono mt-1 break-all">{JSON.stringify(health?.detail?.body || health?.detail || {}).slice(0, 300)}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Token URL</p>
            <p className="font-mono text-slate-800 break-all">{cfg?.token_url || '— not set —'}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Client credentials</p>
            <p className="font-mono text-slate-800">{cfg?.client_id_set ? 'client_id set ✓' : 'client_id — not set —'}<br />{cfg?.client_secret_set ? 'client_secret set ✓' : 'client_secret — not set —'}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Receiving Pix key</p>
            <p className="font-mono text-slate-800">{cfg?.pix_key_set ? cfg.pix_key : '— not set —'}</p>
          </div>
        </div>
        <div className="mt-3 border rounded-md p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="itau-webhook-box">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Pix webhook — auto-confirms paid charges</p>
            {webhookInfo?.local_config?.registered ? (
              <p className="text-xs font-mono text-emerald-700 break-all" data-testid="itau-webhook-status-text">
                registered ({webhookInfo.local_config.environment}) → {webhookInfo.local_config.webhook_url}
              </p>
            ) : webhookInfo?.local_config?.sandbox_unsupported ? (
              <p className="text-xs text-slate-500" data-testid="itau-webhook-status-text">
                Registration attempted — Itaú's <span className="font-mono">sandbox</span> doesn't mock PUT /webhook. Call is production-ready; it will register automatically with production credentials.
              </p>
            ) : (
              <p className="text-xs text-slate-500" data-testid="itau-webhook-status-text">Not registered — paid charges need a manual status refresh</p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={registerWebhook} disabled={busy === 'webhook' || !connected} data-testid="itau-webhook-register-btn">
            {busy === 'webhook' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {webhookInfo?.local_config?.registered ? 'Re-register' : 'Register webhook'}
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          Itaú Unibanco (Brazil) via Itaú for Developers — Pix Recebimentos (BRL collection), Pix Pagamentos (BRL payout),
          Câmbio (FX) and Open Finance payment initiation. Sandbox tokens only work on sandbox URLs; production adds mTLS.
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pix Charge */}
        <Card className="p-6" data-testid="itau-pix-charge-card">
          <div className="flex items-center gap-3 mb-4">
            <QrCode className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold">Pix Charge (Receive BRL)</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount (BRL)</Label>
              <Input data-testid="itau-charge-amount-input" type="number" value={chargeForm.amount}
                onChange={(e) => setChargeForm((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Payer name (optional)</Label>
              <Input data-testid="itau-charge-payer-input" value={chargeForm.payer_name}
                onChange={(e) => setChargeForm((p) => ({ ...p, payer_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Payer CPF (optional)</Label>
              <Input data-testid="itau-charge-cpf-input" placeholder="12345678901" value={chargeForm.payer_cpf}
                onChange={(e) => setChargeForm((p) => ({ ...p, payer_cpf: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input data-testid="itau-charge-desc-input" value={chargeForm.description}
                onChange={(e) => setChargeForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          <Button className="mt-4" data-testid="itau-charge-btn" onClick={createCharge} disabled={busy === 'charge'}>
            {busy === 'charge' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <QrCode className="w-4 h-4 mr-1" />}
            Create Pix Charge
          </Button>
          {chargeResult && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1" data-testid="itau-charge-result">
              <p><span className="text-slate-500">Reference:</span> <span className="font-mono">{chargeResult.reference}</span></p>
              <p><span className="text-slate-500">txid:</span> <span className="font-mono break-all">{chargeResult.txid}</span></p>
              {chargeResult.pix_copia_e_cola ? (
                <div className="flex flex-col sm:flex-row items-start gap-3">
                  <div className="bg-white border rounded-lg p-2 shrink-0" data-testid="itau-charge-qr">
                    <QRCodeSVG value={chargeResult.pix_copia_e_cola} size={140} />
                    <p className="text-[9px] text-center text-slate-400 mt-1">Scan to pay (Pix)</p>
                  </div>
                  <div className="flex items-start gap-2 flex-1 w-full">
                    <p className="font-mono break-all flex-1 bg-white border rounded p-2">{chargeResult.pix_copia_e_cola}</p>
                    <div className="flex flex-col gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => copyText(chargeResult.pix_copia_e_cola)} data-testid="itau-copy-pix-btn">
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => downloadReceipt(chargeResult.reference, chargeResult.reference)} data-testid="itau-charge-receipt-btn" title="Download branded PDF receipt">
                        PDF
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-amber-700">Status: {chargeResult.status} — copia-e-cola code appears once Itaú accepts the charge.</p>
              )}
            </div>
          )}
        </Card>

        {/* Pix Send */}
        <Card className="p-6" data-testid="itau-pix-send-card">
          <div className="flex items-center gap-3 mb-4">
            <Send className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold">Pix Payment (Send BRL)</h3>
            <Badge className="bg-orange-50 text-orange-700 border border-orange-200 uppercase">{cfg?.env || 'sandbox'}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount (BRL)</Label>
              <Input data-testid="itau-send-amount-input" type="number" value={sendForm.amount}
                onChange={(e) => setSendForm((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Destination Pix key (chave)</Label>
              <Input data-testid="itau-send-chave-input" placeholder="CPF / email / phone / EVP" value={sendForm.chave}
                onChange={(e) => setSendForm((p) => ({ ...p, chave: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Recipient name</Label>
              <Input data-testid="itau-send-recipient-input" value={sendForm.recipient_name}
                onChange={(e) => setSendForm((p) => ({ ...p, recipient_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input data-testid="itau-send-desc-input" value={sendForm.description}
                onChange={(e) => setSendForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          <Button className="mt-4" data-testid="itau-send-btn" onClick={sendPix} disabled={busy === 'send'}>
            {busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Send Pix
          </Button>
        </Card>

        {/* FX Quote */}
        <Card className="p-6" data-testid="itau-fx-card">
          <div className="flex items-center gap-3 mb-4">
            <Landmark className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold">FX Quote (Câmbio)</h3>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>From</Label>
              <Select value={fxForm.from_currency} onValueChange={(v) => setFxForm((p) => ({ ...p, from_currency: v }))}>
                <SelectTrigger data-testid="itau-fx-from-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FX_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>To</Label>
              <Select value={fxForm.to_currency} onValueChange={(v) => setFxForm((p) => ({ ...p, to_currency: v }))}>
                <SelectTrigger data-testid="itau-fx-to-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FX_CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <Input data-testid="itau-fx-amount-input" type="number" value={fxForm.amount}
                onChange={(e) => setFxForm((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} />
            </div>
          </div>
          <Button className="mt-4" data-testid="itau-fx-btn" onClick={getFxQuote} disabled={busy === 'fx'}>
            {busy === 'fx' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Get Quote
          </Button>
          {fxResult && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="itau-fx-result">
              {fxResult.status === 'ok' ? (
                <pre className="whitespace-pre-wrap font-mono">{JSON.stringify(fxResult.quote, null, 2).slice(0, 600)}</pre>
              ) : (
                <p className="text-amber-700">FX API returned <span className="font-mono">{fxResult.status}{fxResult.http_code ? ` (HTTP ${fxResult.http_code})` : ''}</span> — the Câmbio product requires access on your Itaú portal profile (company/CNPJ) and its sandbox is currently reporting instability.</p>
              )}
            </div>
          )}
        </Card>

        {/* Open Finance */}
        <Card className="p-6" data-testid="itau-of-card">
          <div className="flex items-center gap-3 mb-4">
            <Server className="w-5 h-5 text-orange-600" />
            <h3 className="text-lg font-semibold">Open Finance Payment Initiation</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount (BRL)</Label>
              <Input data-testid="itau-of-amount-input" type="number" value={ofForm.amount}
                onChange={(e) => setOfForm((p) => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Creditor Pix key</Label>
              <Input data-testid="itau-of-key-input" value={ofForm.creditor_pix_key}
                onChange={(e) => setOfForm((p) => ({ ...p, creditor_pix_key: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Recipient name</Label>
              <Input data-testid="itau-of-recipient-input" value={ofForm.recipient_name}
                onChange={(e) => setOfForm((p) => ({ ...p, recipient_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input data-testid="itau-of-desc-input" value={ofForm.description}
                onChange={(e) => setOfForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          <Button className="mt-4" data-testid="itau-of-btn" onClick={initiateOfPayment} disabled={busy === 'of'}>
            {busy === 'of' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Server className="w-4 h-4 mr-1" />}
            Initiate Payment
          </Button>
        </Card>
      </div>

      {/* Transactions */}
      <Card className="p-6" data-testid="itau-transactions-card">
        <h3 className="text-xl font-semibold mb-4">Itaú Transactions</h3>
        {txns.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="itau-transactions-empty">No Itaú transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="itau-transactions-table">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-2 py-1.5">Date (UTC)</th>
                  <th className="text-left px-2 py-1.5">Reference</th>
                  <th className="text-left px-2 py-1.5">Type</th>
                  <th className="text-right px-2 py-1.5">Amount</th>
                  <th className="text-left px-2 py-1.5">Counterparty</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-left px-2 py-1.5">Env</th>
                  <th className="text-center px-2 py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-2 py-1.5 font-mono text-[10px]">{(t.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-slate-500">{t.reference}</td>
                    <td className="px-2 py-1.5">{(t.type || '').replace(/_/g, ' ')}</td>
                    <td className="px-2 py-1.5 text-right font-mono">R$ {Number(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-2 py-1.5">{t.recipient_name || t.payer_name || '-'}</td>
                    <td className="px-2 py-1.5">
                      {txnStatusChip(t.status)}
                      {t.itau_status ? <span className="ml-1 text-[9px] text-slate-400 font-mono">{t.itau_status}</span> : null}
                      {t.type === 'pix_charge' && t.status === 'created' && t.expires_at && (
                        <span data-testid={`itau-expiry-${t.id}`} className="block text-[9px] text-amber-600 font-mono mt-0.5 flex items-center gap-0.5">
                          <Clock className="w-2.5 h-2.5" /> {(() => { const r = fmtRemaining(t.expires_at, nowMs); return r === 'expiring…' ? r : `expires in ${r}`; })()}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5"><span className="text-[9px] uppercase font-mono text-slate-400">{t.environment}</span></td>
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">
                      {t.type === 'pix_charge' && t.status === 'expired' && !t.reissued_to && (
                        <button onClick={() => reissueCharge(t.id)} data-testid={`itau-reissue-${t.id}`}
                          className="text-[10px] underline text-emerald-700 hover:text-emerald-900 mr-2" disabled={busy === `reissue-${t.id}`}>
                          {busy === `reissue-${t.id}` ? '…' : 'Re-issue'}
                        </button>
                      )}
                      {t.reissued_to && (
                        <span className="text-[9px] text-slate-400 font-mono mr-2" title={`Re-issued as ${t.reissued_to}`}>→ {t.reissued_to}</span>
                      )}
                      {(t.type === 'pix_charge' || (t.type === 'pix_send' && t.txid)) && (
                        <button onClick={() => refreshStatus(t.id)} data-testid={`itau-status-${t.id}`}
                          className="text-[10px] underline text-orange-700 hover:text-orange-900 mr-2" disabled={busy === `status-${t.id}`}>
                          {busy === `status-${t.id}` ? '…' : 'Refresh'}
                        </button>
                      )}
                      <button onClick={() => downloadReceipt(t.id, t.reference)} data-testid={`itau-receipt-${t.id}`}
                        className="text-[10px] underline text-orange-700 hover:text-orange-900 mr-2">
                        Receipt
                      </button>
                      <button onClick={() => downloadDtr(t.id, t.reference)} data-testid={`itau-dtr-${t.id}`}
                        className="text-[10px] underline text-indigo-700 hover:text-indigo-900">
                        DTR
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Production Go-Live Checklist */}
      {checklist && (
        <Card className="p-6" data-testid="itau-prod-checklist-card">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-1 h-8 bg-slate-800 rounded-full" />
              <h3 className="text-xl font-semibold">Production Go-Live Checklist</h3>
            </div>
            {checklist.ready_for_production ? (
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="itau-prod-ready-badge">READY FOR PRODUCTION</Badge>
            ) : (
              <Badge className="bg-amber-50 text-amber-800 border border-amber-200" data-testid="itau-prod-ready-badge">
                {checklist.pending_items?.length || 0} STEP{(checklist.pending_items?.length || 0) === 1 ? '' : 'S'} REMAINING
              </Badge>
            )}
          </div>
          <div className="space-y-2">
            {(checklist.items || []).map((it, idx) => (
              <div key={it.id} className="flex items-start gap-3 border rounded-md p-3" data-testid={`itau-checklist-${it.id}`}>
                {it.status === 'done' ? (
                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                ) : it.status === 'ready' ? (
                  <CheckCircle className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                ) : (
                  <Clock className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    <span className="text-slate-400 font-mono text-xs mr-1.5">{idx + 1}.</span>{it.label}
                    <span className={`ml-2 text-[9px] uppercase font-mono px-1.5 py-0.5 rounded ${it.status === 'done' ? 'bg-emerald-50 text-emerald-700' : it.status === 'ready' ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-700'}`}>{it.status}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{it.detail}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-3">
            When every item is done, go-live is a single env switch (ITAU_ENV=production) — the code paths are identical.
          </p>
        </Card>
      )}
    </div>
  );
};

export default ItauLayer;
